import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FREE, buildJourney, connectorsFor, firstOpenSlot, journeyProgress,
  selectedIds, selectionFromSaved,
  type JourneyOption, type JourneySlot,
} from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import { btn, divider, kicker, t } from '../styles';
import type { ScreenProps } from './types';

/**
 * Build your day.
 *
 * One decision per time band, drawn as an org chart: a dotted trunk drops from
 * the slot above and fans out to the options running in that band. Where only
 * one option matches the attendee's interests it arrives already chosen; where
 * several do, they choose, and the view pans to the next open slot.
 *
 * The split matters. A fully automatic plan gives people something to argue
 * with rather than something they made, and a fully manual one is 19 decisions
 * on a phone. This is the middle: the app resolves the obvious, the attendee
 * spends their attention on the rest.
 */
export function Journey({ bundle, state, dispatch }: ScreenProps) {
  const slots = useMemo(
    () => buildJourney(bundle, state.day, state.interests),
    [bundle, state.day, state.interests],
  );

  const [selection, setSelection] = useState<Record<string, string | null>>(() =>
    selectionFromSaved(slots, state.journey[state.day] ?? []),
  );

  // A day change rebuilds the whole plan, so the selection has to follow.
  useEffect(() => {
    setSelection(selectionFromSaved(slots, state.journey[state.day] ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.day, slots]);

  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const progress = journeyProgress(slots, selection);
  const openIndex = firstOpenSlot(slots, selection);

  const choose = (slot: JourneySlot, sessionId: string, index: number) => {
    const next = { ...selection, [slot.id]: selection[slot.id] === sessionId ? null : sessionId };
    setSelection(next);

    // Pan to the next undecided slot, not simply the next one — skipping past
    // rows that resolved themselves is the whole point of pre-selection.
    const following = slots.slice(index + 1).find((s) => !next[s.id]);
    const target = following ? rowRefs.current[following.id] : null;
    if (target) {
      window.setTimeout(
        () => target.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        180,
      );
    }
  };

  const commit = () => {
    const ids = selectedIds(selection);
    dispatch({ type: 'setJourney', day: state.day, sessionIds: ids });
    // Everything chosen is starred, so the plan shows up on the schedule and in
    // Me rather than living only in this screen.
    for (const id of ids) {
      if (!state.saved.includes(id)) dispatch({ type: 'toggleSave', id });
    }
    dispatch({ type: 'go', screen: 'schedule' });
  };

  if (!slots.length) {
    return (
      <div className="stagger">
        <div style={{ fontSize: 13, color: t(0.55) }}>
          Nothing is programmed on this day yet.
        </div>
      </div>
    );
  }

  return (
    <div className="stagger">
      <div style={{ display: 'flex', gap: 8, paddingBottom: 14 }}>
        {bundle.days.map((d) => {
          const on = state.day === d.key;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => dispatch({ type: 'setDay', day: d.key })}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer',
                border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                background: on ? 'rgba(var(--color-accent-rgb), 0.16)' : 'transparent',
                color: on ? 'var(--color-text)' : t(0.55),
                font: '500 12px/1 var(--font-body)',
              }}
            >
              {d.dow}
            </button>
          );
        })}
      </div>

      <div>
        <p style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.55), margin: '0 0 12px' }}>
          {bundle.strings.journeyIntro}
        </p>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px',
            borderRadius: 9, border: `1px solid ${divider}`, marginBottom: 4,
          }}
        >
          <div style={{ flex: 1, height: 4, borderRadius: 2, background: t(0.1), overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', width: `${progress.pct}%`, borderRadius: 2,
                background: 'var(--color-accent)',
                transition: 'width .35s cubic-bezier(.2,.8,.2,1)',
              }}
            />
          </div>
          <div style={{ font: '400 10.5px/1 var(--font-mono)', color: t(0.5) }}>
            {progress.decided}/{progress.total}
          </div>
        </div>
      </div>

      <div>
        {slots.map((slot, i) => (
          <SlotRow
            key={slot.id}
            slot={slot}
            index={i}
            isOpen={i === openIndex}
            selectedId={selection[slot.id]}
            emptyCopy={bundle.strings.journeyEmptySlot}
            onChoose={(id) => choose(slot, id, i)}
            innerRef={(el) => { rowRefs.current[slot.id] = el; }}
          />
        ))}
      </div>

      <div style={{ position: 'sticky', bottom: 0, paddingTop: 10, paddingBottom: 6 }}>
        <div
          style={{
            padding: 13, borderRadius: 11,
            background: 'var(--color-surface)', boxShadow: 'var(--shadow-md)',
          }}
        >
          <div style={{ fontSize: 12, color: t(0.6), marginBottom: 10 }}>
            {openIndex === -1
              ? bundle.strings.journeyDone
              : `${progress.total - progress.decided} slot${progress.total - progress.decided === 1 ? '' : 's'} left to decide.`}
          </div>
          <button
            type="button"
            onClick={commit}
            style={btn('primary', { width: '100%', height: 46, borderRadius: 10, fontSize: 14.5 })}
          >
            Save {progress.decided} to my schedule
          </button>
        </div>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}

function SlotRow({
  slot,
  index,
  isOpen,
  selectedId,
  emptyCopy,
  onChoose,
  innerRef,
}: {
  slot: JourneySlot;
  index: number;
  isOpen: boolean;
  selectedId: string | null;
  emptyCopy: string;
  onChoose: (id: string) => void;
  innerRef: (el: HTMLDivElement | null) => void;
}) {
  const connectors = connectorsFor(slot.options.length);

  return (
    <div ref={innerRef} style={{ scrollMarginTop: 90 }}>
      <Connectors connectors={connectors} first={index === 0} />

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 9, margin: '2px 0 9px',
        }}
      >
        <div style={kicker(10, isOpen ? 'var(--color-accent-300)' : t(0.42), 0.14)}>
          {slot.label}
        </div>
        {slot.auto && (
          <span
            style={{
              font: '500 9px/1 var(--font-body)', letterSpacing: '0.1em',
              textTransform: 'uppercase', padding: '3px 7px', borderRadius: 4,
              background: 'var(--color-accent-800)', color: 'var(--color-accent-200)',
            }}
          >
            Picked for you
          </span>
        )}
        <div
          style={{
            flex: 1, height: 1,
            background: `linear-gradient(to right, ${divider}, transparent)`,
          }}
        />
      </div>

      <div style={{ display: 'grid', gap: 7, marginBottom: 4 }}>
        {slot.options.map((option) => (
          <OptionBox
            key={option.session.id}
            option={option}
            selected={selectedId === option.session.id}
            dimmed={!!selectedId && selectedId !== option.session.id}
            onSelect={() => onChoose(option.session.id)}
          />
        ))}
        <FreeBox
          selected={selectedId === FREE}
          dimmed={!!selectedId && selectedId !== FREE}
          onSelect={() => onChoose(FREE)}
        />
      </div>

      {!selectedId && (
        <div style={{ fontSize: 11, lineHeight: 1.5, color: t(0.38), margin: '2px 0 4px' }}>
          {emptyCopy}
        </div>
      )}
    </div>
  );
}

/**
 * The dotted fan.
 *
 * A trunk drops from the row above, then branches out to each option. Branch
 * stroke width falls off with distance from the trunk, so the fan reads as a
 * diagram rather than a comb of identical lines.
 */
function Connectors({ connectors, first }: { connectors: ReturnType<typeof connectorsFor>; first: boolean }) {
  const height = first ? 18 : 34;
  const trunkY = first ? 0 : 15;

  return (
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      aria-hidden
      style={{ display: 'block', width: '100%', height, overflow: 'visible' }}
    >
      {!first && (
        <line
          x1="50" y1="0" x2="50" y2={trunkY}
          stroke="var(--color-text)" strokeOpacity={0.22}
          strokeWidth={1.4} strokeDasharray="3 3" vectorEffect="non-scaling-stroke"
        />
      )}
      {connectors.map((c, i) => (
        <path
          key={i}
          d={`M 50 ${trunkY} L 50 ${trunkY + 10} L ${c.x * 100} ${trunkY + 14} L ${c.x * 100} 40`}
          fill="none"
          stroke={i === 0 ? 'var(--color-accent)' : 'var(--color-text)'}
          strokeOpacity={i === 0 ? 0.5 : 0.2}
          strokeWidth={c.width}
          strokeDasharray={`${c.dash} ${c.dash}`}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

/** "Keep this free" — a real plan, not a missing one. */
function FreeBox({
  selected, dimmed, onSelect,
}: {
  selected: boolean; dimmed: boolean; onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
        padding: '10px 13px', borderRadius: 10, cursor: 'pointer',
        border: `1px dashed ${selected ? 'var(--color-accent)' : divider}`,
        background: selected ? 'rgba(var(--color-accent-rgb), 0.1)' : 'transparent',
        color: 'var(--color-text)',
        opacity: dimmed ? 0.4 : 1,
        transition: 'opacity .2s ease, border-color .2s ease, background .2s ease',
      }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: t(selected ? 0.8 : 0.5) }}>
        Keep this time free
      </span>
      <span
        style={{
          flex: 'none', width: 20, height: 20, borderRadius: '50%',
          border: `1.5px solid ${selected ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.22)'}`,
          background: selected ? 'var(--color-accent)' : 'transparent',
          boxShadow: selected ? 'inset 0 0 0 4px var(--color-bg)' : 'none',
          transition: 'all .18s ease',
        }}
      />
    </button>
  );
}

function OptionBox({
  option,
  selected,
  dimmed,
  onSelect,
}: {
  option: JourneyOption;
  selected: boolean;
  dimmed: boolean;
  onSelect: () => void;
}) {
  const theme = useTheme();
  const { session } = option;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
        padding: '11px 13px', borderRadius: 10, cursor: 'pointer',
        border: `1px solid ${selected ? 'var(--color-accent)' : divider}`,
        background: selected ? 'rgba(var(--color-accent-rgb), 0.14)' : 'var(--color-surface)',
        color: 'var(--color-text)',
        opacity: dimmed ? 0.45 : 1,
        transition: 'opacity .2s ease, border-color .2s ease, background .2s ease, transform .16s cubic-bezier(.2,.8,.2,1)',
        transform: selected ? 'none' : undefined,
      }}
    >
      <span
        style={{
          flex: 'none', width: 2, alignSelf: 'stretch', borderRadius: 2,
          background: trackColor(theme, session.track),
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: '500 14px/1.25 var(--font-body)' }}>
          {session.title}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: t(0.5), marginTop: 4 }}>
          {session.t}–{session.end} · {session.room}
        </span>
        <span
          style={{
            display: 'block', fontSize: 11, marginTop: 5,
            color: option.matches ? 'var(--color-accent-300)' : t(0.4),
          }}
        >
          {option.reason}
        </span>
      </span>
      <span
        style={{
          flex: 'none', width: 20, height: 20, borderRadius: '50%',
          border: `1.5px solid ${selected ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.22)'}`,
          background: selected ? 'var(--color-accent)' : 'transparent',
          boxShadow: selected ? 'inset 0 0 0 4px var(--color-surface)' : 'none',
          transition: 'all .18s ease',
        }}
      />
    </button>
  );
}
